import { Lemonade, setConfig, useLemonadeQuestion } from '@oneclick/react-lemonade-editor';
import { CONFIG } from 'app/config/config';
import { defaultConfig } from 'app/config/environment';
import { useGoogleClientId } from 'app/hooks/useGoogleClientId';
import clsx from 'clsx';
import { uploadContentToJob } from 'core/crud/contents.crud';
import useInertNodes from 'core/hooks/useInertNodes';
import { useTranslate } from 'core/hooks/useTranslate';
import { useUser } from 'core/lite/hooks/useUser';
import AssessmentGradeTag from 'core/modules/compositions/AssessmentGradeTag';
import getBlockedFileDropProps from 'core/utils/blockedFileDrop';
import {
  LEMONADE_MAX_UPLOAD_FILE_SIZE_MB,
  LEMONADE_QUESTION_TYPES,
  LEMONADE_SCORING_TYPE,
  LEMONADE_UPLOAD_CAPTURE_SELECTOR,
  LEMONADE_UPLOAD_QUESTION_TYPES,
} from 'core/utils/lemonade';
import { JOB_STATUS, SUBMITTED_JOB_STATUSES } from 'core/variables/jobs';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Fix TAN-8584: rbd's onTouchStart (React synthetic prop) fires at the React root
// in bubble phase. A native listener here intercepts it first.
// .fr-audio has pointer-events:none for desktop compatibility, which causes iOS to
// misdirect the synthesized click (touchstart→touchend→click) to drag-card content
// instead of the audio button. We prevent default to stop that bad synthesized click,
// then dispatch a programmatic click on the correct audio button ourselves (still
// inside the user-gesture handler so audio playback is allowed by the browser).
function triggerAudioButton(frAudioEl) {
  const iconBtn = frAudioEl.querySelector('button.lemo-fr-button-audio');
  if (iconBtn) {
    iconBtn.click();
    return;
  }
  const toggleBtn = frAudioEl.querySelector('.toggle-play');
  if (toggleBtn) {
    toggleBtn.click();
    return;
  }
  // Fallback for other pointer-events:auto children (player, transcription icon)
  const fallbackBtn = frAudioEl.querySelector('button, [role="button"]');
  if (fallbackBtn) {
    fallbackBtn.click();
  }
}

function handleAudioTouchStart(e) {
  const touch = e.touches[0];
  if (!touch) return;

  // Fast path: touch landed on a pointer-events:auto child of .fr-audio.
  // Intercept everywhere: stopPropagation prevents rbd from capturing the touch as a
  // drag; on non-drag question types it is a no-op. preventDefault + programmatic click
  // produces the same result as the native click would have.
  const directAudio = e.target.closest('.fr-audio');
  if (directAudio) {
    e.stopPropagation();
    e.preventDefault();
    triggerAudioButton(directAudio);
    return;
  }

  // Fallback: touch passed through pointer-events:none .fr-audio outer area to content
  // beneath it (only possible inside a drag card where rbd is present).
  const audioEls = e.currentTarget.querySelectorAll('.fr-audio');
  for (const el of audioEls) {
    const rect = el.getBoundingClientRect();
    if (touch.clientX >= rect.left && touch.clientX <= rect.right && touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
      e.stopPropagation();
      e.preventDefault();
      triggerAudioButton(el);
      return;
    }
  }
}

function LemonadeAudioWrapper({ children, isReadOnly = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('touchstart', handleAudioTouchStart, { passive: false });
    return () => el.removeEventListener('touchstart', handleAudioTouchStart);
  }, []);
  useInertNodes(ref, isReadOnly, LEMONADE_UPLOAD_CAPTURE_SELECTOR);
  const blockedFileDropProps = getBlockedFileDropProps(isReadOnly);
  return (
    <div className={clsx('lemonade-audio-wrapper', { 'lemonade-audio-wrapper--read-only': isReadOnly })} ref={ref} {...blockedFileDropProps}>
      {children}
    </div>
  );
}

LemonadeAudioWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  isReadOnly: PropTypes.bool,
};

export { handleAudioTouchStart, LemonadeAudioWrapper, triggerAudioButton };

export function useLemonadeViewer({
  data,
  studentResponse,
  disableInteraction,
  onResponseChange,
  onSubmit,
  score,
  contentGuid,
  jobGuid,
  uploadURL,
  canStudentEditResponses = false,
  scoreScale,
  onErrorToUploadFileAndAudio,
  evaluationScale,
  jobStatus,
}) {
  const { isTeacher, isEditorial, isStudent } = useUser();
  const [response, setResponse] = useState(false);
  const [hints, setHints] = useState(false);
  const [hasUploadToken, setHasUploadToken] = useState(uploadURL);
  const [isRepeating, setIsRepeating] = useState(false);
  const { t } = useTranslate();

  const googleClientId = useGoogleClientId();

  setConfig({
    googlePicker: {
      clientId: googleClientId,
      developerKey: CONFIG.googleKeys?.DEVELOPER_KEY,
    },
    mathRenderEngine: 'mathjax',
    showSampleAnswer: true,
    maxUploadFileSize: LEMONADE_MAX_UPLOAD_FILE_SIZE_MB,
  });

  const {
    exercise: { Question, Controls, questionApi: store, SampleAnswer },
    updateExercise,
  } = useLemonadeQuestion({
    data: {
      data: data?.data?.data || data?.data,
      reference: data?.reference,
      metadata: data?.metadata,
      type: data?.type,
      title: data?.title,
    },
    lang: i18next.language.split('-')[0],
    assetsQuestionApi: uploadURL,
  });

  const generateUploadTicket = async () => {
    try {
      let response = await uploadContentToJob(jobGuid);
      if (response.status === 200) {
        const { endpoint, token } = response.data.data.data.upload;
        store.setAssetsQuestionApi(defaultConfig.BASE_URL_.slice(0, -1) + endpoint + '/' + token);
      } else if (['localhost:3003'].includes(window.location.host)) {
        let token =
          'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJ1cGxvYWQiOnsibWF4LXNpemUiOm51bGwsIm1pbWUiOm51bGwsImNiIjoiaHR0cHM6XC9cL3RhbmdlcmluZWF3cy50a1wvYXBpXC9mcm9udFwvam9ic1wvZmM4ZTQxNzAtMjkwNS0xMWVkLWE2NjQtNjc3NDM4ZTA4MTBmXC9maWxlc1wvZWQyNGJlOTAtMmU5MC0xMWVkLThjNjQtMGYyNzYzZjk5ZTA2XC91cGxvYWQtY29tcGxldGU_cmVmZXJlcj1lZGVsdml2ZXNkaWdpdGFsLWRldi5vbmVjbGlja2F3cy50ayIsInJlZiI6ImVkMjRiZTkwLTJlOTAtMTFlZC04YzY0LTBmMjc2M2Y5OWUwNiIsInB1YmxpYyI6dHJ1ZSwiZGVjb21wcmVzcyI6ZmFsc2UsInBhdGgiOm51bGwsImZvcmNlLXVwbG9hZCI6ZmFsc2UsImZvbGRlciI6ImVkMjRiZTkwLTJlOTAtMTFlZC04YzY0LTBmMjc2M2Y5OWUwNiIsImNvbnZlcnQteG9kIjpmYWxzZSwiaW5uZXItZm9sZGVyIjoiIiwidXBsb2FkZXIiOiJmcm9hbGEiLCJsb2NhbCI6ZmFsc2V9LCJpYXQiOjE2NjI1NDM1NTB9.kDMyI2Alr35nIze8u3ZVONtqjEB3lMfnrf8W2sZW4pk';
        let endpoint = '/files-storage/bundles/727dfced-8994-4d44-8dd0-86effa18d87e/files';

        store.setAssetsQuestionApi(defaultConfig.BASE_URL_.slice(0, -1) + endpoint + '/' + token);
      }
      setHasUploadToken(true);
    } catch (error) {
      console.log(error, 'error generateUploadTicket');
    }
  };

  // Generate upload tickets when the question type requires file uploading
  // The contentGuid is recommended to generate an upload ticket
  useEffect(() => {
    if (LEMONADE_UPLOAD_QUESTION_TYPES.includes(data?.type) && contentGuid && !uploadURL) {
      generateUploadTicket();
    }
  }, [data?.type, contentGuid]);

  const getHints = () => {
    let hint = [];
    try {
      hint = data?.data?.hints;
    } catch (e) {
      console.log(e, 'error getHint');
    }

    return hint;
  };

  useEffect(() => {
    if (studentResponse) {
      showStudentResponse();
    }
  }, []);

  useEffect(() => {
    if (!updateExercise) return;
    setHints(getHints());
    if (disableInteraction) {
      if (studentResponse) {
        setTimeout(() => {
          showStudentResponse();
        }, 100);
      }
    }
  }, [updateExercise, disableInteraction]);

  const resetQuestion = () => {
    try {
      store.reset();
    } catch (e) {
      console.log(e, 'error resetQuestion');
    }
  };

  const showCorrectAnswer = () => {
    if (store.isEvaluable) {
      // Lemonade solo guarda la respuesta del alumno en caché al evaluar la pregunta, y al mostrar soluciones
      // la lee por elemento: sin respuesta del alumno, «unir con líneas» en móvil revienta
      // (TANGERINE-FRONTOFFICE-11N). Si ya estaba guardada, guardarla otra vez da el mismo valor.
      store.saveCache();
      store.showSolutions();
      store.checkAnswers();
    }
  };

  const showEvaluatedAnswer = () => {
    if (!isStudent || jobStatus === JOB_STATUS.EVALUATED) {
      store?.checkAnswers();
    }
  };

  const showStudentResponse = (response) => {
    if (!response) store?.reset();
    if (response || studentResponse) store?.setResponse(response !== undefined ? response : studentResponse);
    window.store = store;
    if (!canStudentEditResponses && !LEMONADE_UPLOAD_QUESTION_TYPES.includes(data?.type)) {
      showEvaluatedAnswer();

      if (!store.isEvaluable && store?.send) {
        store.send();
      }
    }
  };

  const setApiConfig = (endpoint) => {
    store.setAssetsQuestionApi(endpoint);
  };

  const DisplayQuestionComponent = useCallback(() => {
    return (
      <LemonadeAudioWrapper>
        <Lemonade className={disableInteraction ? 'lemonade-disable-interaction' : ''}>
          {store.isEvaluable ? (
            <Controls checkAnswers={disableInteraction} disabled={disableInteraction} showCheckAnswer={!disableInteraction}>
              <Question
                changed={(output) => {
                  setResponse(output.response ? output.response : store.getResponse());
                }}
              />
            </Controls>
          ) : (
            <Controls showHints={true} disabled={disableInteraction} showEdit={!disableInteraction}>
              <Question
                changed={(output) => {
                  setResponse(output.response || store.getResponse());
                }}
              />
              {(isTeacher || isEditorial) && <SampleAnswer />}
            </Controls>
          )}
        </Lemonade>
      </LemonadeAudioWrapper>
    );
  }, [studentResponse, store, disableInteraction]);

  const AnswerQuestionComponent = useCallback(() => {
    return (
      <LemonadeAudioWrapper>
        <Lemonade>
          {data?.type === 'uploadfile' || data?.type === 'pencil' || data?.type === 'audiorecorder' ? (
            <Controls
              checkAnswers={disableInteraction}
              disabled={disableInteraction}
              showCheckAnswer={!disableInteraction}
              onSend={data?.type === 'pencil' || data?.type === 'audiorecorder' ? (e) => setResponse({ [e.reference]: store.getResponse() }) : null}
            >
              <Question
                onUploadComplete={
                  LEMONADE_UPLOAD_QUESTION_TYPES.includes(data?.type)
                    ? (e) => {
                        setResponse({ [e.reference]: e });
                      }
                    : null
                }
                changed={(output) => {
                  setResponse(output.response || store.getResponse());
                }}
                {...(studentResponse ? { response: studentResponse } : {})}
              />
            </Controls>
          ) : (
            <Question
              disabled={disableInteraction}
              changed={(output) => {
                setResponse(output.response || store.getResponse());
              }}
              {...(studentResponse ? { response: studentResponse } : {})}
            />
          )}
        </Lemonade>
      </LemonadeAudioWrapper>
    );
  }, [disableInteraction]);

  function renderScore(points) {
    if (store.isEvaluable) {
      if (points !== undefined) {
        return (
          <div className="lemonade-exercises__score">
            <AssessmentGradeTag
              grade={`${Math.round(points * 10)}`}
              gradeTotal="10"
              isPending={false}
              t={t}
              scoreScale={scoreScale}
              evaluationScale={evaluationScale}
            />
          </div>
        );
      }
      // jobStatus is the raw job status ('evaluated'), not the display status ('assessment-evaluated') used in upper layers
    } else if (!isRepeating && !Number.isNaN(score) && score !== null && jobStatus === JOB_STATUS.EVALUATED) {
      return (
        <div className="lemonade-exercises__score">
          <AssessmentGradeTag grade={score} gradeTotal="10" isPending={false} t={t} scoreScale={scoreScale} evaluationScale={evaluationScale} />
        </div>
      );
    } else if (studentResponse) {
      return (
        <div className="lemonade-exercises__score">
          <AssessmentGradeTag isPending={true} evaluationScale={evaluationScale} />
        </div>
      );
    }
  }

  const isQuestionDisabled = disableInteraction || SUBMITTED_JOB_STATUSES.includes(jobStatus);
  // Lemonade's `disabled` prop does not reach the uploadfile/audiorecorder dropzone nor its microphone
  const isUploadQuestionReadOnly = isQuestionDisabled && LEMONADE_UPLOAD_QUESTION_TYPES.includes(data?.type);

  const LemonadeDefaultQuestionWithControls = useCallback(() => {
    return (
      <LemonadeAudioWrapper isReadOnly={isUploadQuestionReadOnly}>
        <Lemonade className={` ${store.isEvaluable ? '' : 'hide-reset'}`}>
          <Controls
            showPoints
            showCheckAnswer
            // Repeat/Reset wipe an already delivered response and lemonade keeps them clickable despite `disabled`
            showReset={store.isEvaluable && !isUploadQuestionReadOnly}
            showRepeat={store.isEvaluable && !isUploadQuestionReadOnly}
            showEdit={store.isEvaluable}
            showUpload
            disabled={isQuestionDisabled}
            onRepeat={() => {
              if (!store.isEvaluable) {
                setIsRepeating(true);
                renderScore();
              }
            }}
            renderScore={renderScore}
            onValidate={() => onSubmit(null, store)}
            onSend={(e) => {
              if (!LEMONADE_UPLOAD_QUESTION_TYPES.includes(data?.type)) {
                if (data?.type === LEMONADE_QUESTION_TYPES.STATEMENT) {
                  const statementResponse = {
                    reference: data?.reference,
                    value: '',
                    type: LEMONADE_QUESTION_TYPES.STATEMENT,
                    scoring_type: LEMONADE_SCORING_TYPE.MANUAL,
                    score: 0,
                    max_score: 0,
                  };
                  onSubmit(statementResponse, store);
                } else {
                  onSubmit(e, store);
                }
              }
            }}
          >
            <Question
              onUploadComplete={(e) => {
                if (hasUploadToken) {
                  onSubmit(e, store);
                }
              }}
              onUploadError={(e) => onErrorToUploadFileAndAudio(e, store)}
              changed={(output) => {
                setResponse(output.response || store.getResponse());
              }}
            />
          </Controls>
        </Lemonade>
      </LemonadeAudioWrapper>
    );
  }, [store, studentResponse, hasUploadToken, onSubmit, isQuestionDisabled, isUploadQuestionReadOnly]);

  return {
    store,
    hints,
    response,
    Question: DisplayQuestionComponent,
    AnswerQuestion: AnswerQuestionComponent,
    LemonadeDefaultQuestionWithControls,
    setApiConfig,
    resetQuestion,
    onResponseChange,
    showCorrectAnswer,
    showStudentResponse,
  };
}

useLemonadeViewer.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string,
    data: PropTypes.object,
    reference: PropTypes.string,
    metadata: PropTypes.object,
    title: PropTypes.string,
  }),
  studentResponse: PropTypes.object,
  disableInteraction: PropTypes.bool,
  onResponseChange: PropTypes.func,
  onSubmit: PropTypes.func,
  jobStatus: PropTypes.string,
};

useLemonadeViewer.defaultProps = {
  disableInteraction: false,
  studentResponse: false,
  data: {
    title: '',
    type: 'mcq',
    metadata: {
      name: 'Multiple choice – standard',
    },
    data: {
      options: [
        {
          label: '<p>El área de un cilindro.</p>',
          value: '0',
          feedback: '',
        },
        {
          label: '<p>El volumen de un cilindro.</p>',
          value: '1',
          feedback: '',
        },
        {
          label: '<p>El área de la base de un cilindro.</p>',
          value: '2',
          feedback: '',
        },
      ],
      stimulus: '<p>¿Qué pide el problema que calcules?</p>',
      type: 'mcq',
      validation: {
        scoring_type: 'exactMatch',
        immediateFeedback: false,
        alt_responses: [],
        valid_response: {
          score: 1,
          value: ['1'],
        },
      },
      ui_style: {
        type: 'Multiple choice – standard',
        columns: 1,
        labelType: '',
        customClass: '',
        inlineFeedback: false,
      },
      isEvaluable: true,
      attempts: {
        attempts: 0,
      },
      hints: [],
      feedback: {
        successMessage: '',
        errorMessages: [],
        responseMessages: [],
        withFeedbackPanel: false,
        config: {
          visibility: true,
          sequence: 'stack',
        },
      },
      shuffle_options: false,
      showCheckIcon: true,
    },
    reference: 'd517245c-3a74-4423-9bc7-3445587bb955',
    extraValues: {},
  },
};
